import React from "react";
import { cn } from "@/lib/utils";

interface PaymentBrandLogoProps {
  brand: string;
  className?: string;
}

/**
 * Payment brand logo component
 * Displays the appropriate payment brand logo based on the brand name using inline SVG
 */
export const PaymentBrandLogo: React.FC<PaymentBrandLogoProps> = ({
  brand,
  className = "",
}) => {
  // Normalize brand name to lowercase for comparison
  const normalizedBrand = brand?.toLowerCase().trim();

  // Card brand icon render helper
  const renderCardIcon = (brandName: string) => {
    const brandLower = (brandName || "").toLowerCase();

    if (brandLower === "visa") {
      return (
        <svg width="40" height="13" viewBox="0 0 40 13" fill="none">
          <path
            d="M15.8 12.24L18.28 0.72H21.64L19.16 12.24H15.8ZM31.16 0.96C30.52 0.72 29.48 0.48 28.28 0.48C24.92 0.48 22.52 2.28 22.52 4.8C22.52 6.72 24.28 7.8 25.64 8.4C27 9 27.48 9.36 27.48 9.96C27.48 10.8 26.52 11.28 25.64 11.28C24.36 11.28 23.64 11.04 22.6 10.56L22.12 10.32L21.64 13.08C22.44 13.44 23.88 13.8 25.4 13.8C29 13.8 31.32 12 31.32 9.36C31.32 7.92 30.36 6.84 28.28 5.88C27.08 5.28 26.36 4.92 26.36 4.32C26.36 3.72 27 3.12 28.36 3.12C29.32 3.12 30.12 3.36 30.76 3.6L31.08 3.72L31.56 0.96H31.16ZM37.4 0.72H34.76C33.88 0.72 33.24 0.96 32.84 1.92L27.96 12.24H31.56L32.36 10.08H36.76L37.24 12.24H40.4L37.4 0.72ZM33.32 7.56C33.56 6.84 34.6 4.08 34.6 4.08C34.6 4.08 34.84 3.48 34.96 3.12L35.16 4.08C35.16 4.08 35.8 6.84 35.92 7.56H33.32ZM13.16 0.72L9.84 8.88L9.48 7.08C8.92 5.16 7.08 3.12 5 2.04L8.04 12.24H11.68L17.08 0.72H13.16Z"
            fill="#1434CB"
          />
          <path
            d="M6.12 0.72H0.28L0.2 1.08C4.44 2.16 7.32 4.8 8.52 7.92L7.32 2.16C7.08 1.2 6.44 0.72 5.56 0.72H6.12Z"
            fill="#F9A51A"
          />
        </svg>
      );
    }

    if (brandLower === "mastercard" || brandLower === "master card") {
      return (
        <svg width="32" height="22" viewBox="0 0 32 22" fill="none">
          <circle cx="11" cy="11" r="11" fill="#EB001B" />
          <circle cx="21" cy="11" r="11" fill="#F79E1B" />
        </svg>
      );
    }

    if (
      brandLower === "american-express" ||
      brandLower === "amex" ||
      brandLower === "american express"
    ) {
      return (
        <svg width="40" height="13" viewBox="0 0 40 13" fill="none">
          <rect width="40" height="13" rx="2" fill="#006FCF" />
          <path
            d="M8.5 4.5L6.5 8.5H8L8.5 7.5L9 8.5H10.5L8.5 4.5ZM12.5 4.5V8.5H14V4.5H12.5ZM16.5 4.5V8.5H18V4.5H16.5ZM20.5 4.5L18.5 8.5H20L20.5 7.5L21 8.5H22.5L20.5 4.5ZM24.5 4.5V8.5H26V4.5H24.5ZM28.5 4.5V8.5H30V4.5H28.5ZM32.5 4.5L30.5 8.5H32L32.5 7.5L33 8.5H34.5L32.5 4.5Z"
            fill="white"
          />
        </svg>
      );
    }

    if (brandLower === "discover" || brandLower === "discover card") {
      return (
        <svg width="40" height="13" viewBox="0 0 40 13" fill="none">
          <rect width="40" height="13" rx="2" fill="#FF6000" />
          <path
            d="M20 6.5C20 7.88 18.88 9 17.5 9C16.12 9 15 7.88 15 6.5C15 5.12 16.12 4 17.5 4C18.88 4 20 5.12 20 6.5Z"
            fill="white"
          />
        </svg>
      );
    }

    if (
      brandLower === "diners club" ||
      brandLower === "diners" ||
      brandLower === "dinersclub"
    ) {
      return (
        <svg width="40" height="13" viewBox="0 0 40 13" fill="none">
          <rect width="40" height="13" rx="2" fill="#0079BE" />
          <text
            x="20"
            y="9"
            textAnchor="middle"
            fill="white"
            fontSize="8"
            fontWeight="bold"
          >
            DINERS
          </text>
        </svg>
      );
    }

    if (brandLower === "jcb") {
      return (
        <svg width="40" height="13" viewBox="0 0 40 13" fill="none">
          <rect width="40" height="13" rx="2" fill="#0E4C96" />
          <text
            x="20"
            y="9"
            textAnchor="middle"
            fill="white"
            fontSize="8"
            fontWeight="bold"
          >
            JCB
          </text>
        </svg>
      );
    }

    if (brandLower === "unionpay" || brandLower === "union pay") {
      return (
        <svg width="40" height="13" viewBox="0 0 40 13" fill="none">
          <rect width="40" height="13" rx="2" fill="#E21836" />
          <text
            x="20"
            y="9"
            textAnchor="middle"
            fill="white"
            fontSize="7"
            fontWeight="bold"
          >
            UnionPay
          </text>
        </svg>
      );
    }

    // Default fallback for unknown brands
    return (
      <svg width="32" height="22" viewBox="0 0 32 22" fill="none">
        <rect
          width="32"
          height="22"
          rx="2"
          fill="#E5E7EB"
          className="dark:fill-gray-600"
        />
        <text
          x="16"
          y="14"
          textAnchor="middle"
          fill="#6B7280"
          className="dark:fill-gray-400"
          fontSize="8"
          fontWeight="bold"
        >
          {brandName?.toUpperCase().slice(0, 4) || "CARD"}
        </text>
      </svg>
    );
  };

  return (
    <div
      className={cn(
        "flex items-center justify-center  border border-gray-100 dark:border-gray-700 rounded overflow-hidden",
        className
      )}
    >
      {renderCardIcon(normalizedBrand)}
    </div>
  );
};
